import { useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Send } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function ComposeEmailDialog({ open, onClose, recipients, onSent }) {
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const token = localStorage.getItem("session_token");

  const handleSend = async () => {
    if (!subject.trim() || !message.trim()) {
      toast.error("Subject and message are required");
      return;
    }
    setSending(true);
    try {
      const res = await axios.post(
        `${API}/admin/emails/send`,
        { subject, message, recipient_ids: recipients.map((r) => r.id) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const { sent_count, failed_count, skipped_count } = res.data;
      toast.success(`Sent to ${sent_count} student${sent_count === 1 ? "" : "s"}${failed_count ? `, ${failed_count} failed` : ""}${skipped_count ? `, ${skipped_count} skipped (invalid email)` : ""}`);
      setSubject("");
      setMessage("");
      onSent?.(res.data);
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send email");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="compose-email-dialog">
        <DialogHeader>
          <DialogTitle>Email {recipients.length} student{recipients.length === 1 ? "" : "s"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="text-xs text-slate-600 max-h-20 overflow-y-auto rounded border p-2 bg-slate-50" data-testid="compose-recipients">
            {recipients.map((r) => r.email).join(", ")}
          </div>
          <Input
            placeholder="Subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            data-testid="compose-subject"
          />
          <Textarea
            placeholder="Write your message... Each student receives their own email starting with 'Dear {name},' — the branded header and footer are added automatically."
            rows={10}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            data-testid="compose-message"
          />
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose} disabled={sending} data-testid="compose-cancel">Cancel</Button>
            <Button onClick={handleSend} disabled={sending} className="gap-2 bg-[#102862] hover:bg-[#1a3a7a] text-white" data-testid="compose-send">
              <Send className="w-4 h-4" />
              {sending ? "Sending..." : `Send to ${recipients.length}`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
